import { useState, useRef, useEffect, KeyboardEvent, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { Rnd, RndResizeCallback } from 'react-rnd';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  makeStyles,
  CircularProgress,
  Avatar,
  Tooltip,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseIcon from '@material-ui/icons/Close';
import MinimizeIcon from '@material-ui/icons/Minimize';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import SendIcon from '@material-ui/icons/Send';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import HistoryIcon from '@material-ui/icons/History';
import AssistantIcon from '@material-ui/icons/Assistant';
import { useChat } from '../../contexts';
import { ChatMessageComponent } from '../ChatMessage';
import { ConversationHistoryModal } from '../ConversationHistory';
import { AssistantSelector } from '../AssistantSelector';
import {
  isBackstageAgentAssistant,
  isRagAssistant,
} from '../../api/LocalAssistants';
import { getAssistantLogoUrl, shouldShowLocalAssistantIconFallback } from '../../util';
import { MessageRole, isAgentProgressMessageId } from '../../api/constants';
import { BackstageAgentSuggestedQuestions } from '../BackstageAgentSuggestedQuestions';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.modal,
  },
  paper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[10],
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[12],
    },
  },
  header: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    '& .MuiIconButton-root': {
      color: theme.palette.primary.contrastText,
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  inputContainer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
  },
  minimizedButton: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.shadows[8],
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scale(1.05)',
      boxShadow: theme.shadows[10],
    },
  },
  minimizedContainer: {
    position: 'relative',
    '&:hover $minimizedClose': {
      opacity: 1,
    },
  },
  minimizedClose: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s',
    cursor: 'pointer',
    border: `2px solid ${theme.palette.background.default}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[700],
    },
  },
  assistantAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
}));

type Size = { width: number; height: number };
type Position = { x: number; y: number };

const MARGIN = 20;
const DEFAULT_SIZE: Size = { width: 450, height: 600 };
const STORAGE_KEY = 'mga-chat-dock-v2';

// Helper functions
function calcBottomRight(size: Size): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(MARGIN, vw - size.width - MARGIN),
    y: Math.max(MARGIN, vh - size.height - MARGIN),
  };
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export interface CollapsibleChatProps {
  isOpen: boolean;
  onClose: () => void;
  onMaximize?: () => void;
  isMinimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
}

export const CollapsibleChat = ({
  isOpen,
  onClose,
  onMaximize,
  isMinimized: isMinimizedProp,
  onMinimize,
  onExpand,
}: CollapsibleChatProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const mounted = useMounted();

  const [storedState, setStoredState] = useLocalStorage<{
    size: Size;
    position: Position;
  }>(STORAGE_KEY, {
    size: DEFAULT_SIZE,
    position: calcBottomRight(DEFAULT_SIZE),
  });

  const [size, setSize] = useState<Size>(storedState?.size || DEFAULT_SIZE);
  const [position, setPosition] = useState<Position>(
    storedState?.position || { x: 0, y: 0 },
  );
  const [localIsMinimized, setLocalIsMinimized] = useState(false);
  const isMinimized = isMinimizedProp ?? localIsMinimized;
  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
      return;
    }
    setLocalIsMinimized(true);
  };
  const handleExpand = () => {
    if (onExpand) {
      onExpand();
      return;
    }
    setLocalIsMinimized(false);
  };

  const [input, setInput] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { activeConversation, sendMessage, pendingAssistant } = useChat();
  const { data: conversation, isLoading } = activeConversation;
  const messages = useMemo(
    () => conversation?.messages || [],
    [conversation?.messages],
  );
  const hasStreamingProgress = useMemo(
    () =>
      messages.some(
        message =>
          message.role === MessageRole.ASSISTANT &&
          isAgentProgressMessageId(message.id),
      ),
    [messages],
  );
  const currentAssistant = conversation?.assistant || pendingAssistant;

  // Initialize position from stored state on mount
  useEffect(() => {
    if (!mounted) return;
    if (storedState) {
      setSize(storedState.size);
      setPosition(calcBottomRight(storedState.size));
    } else {
      const initialPos = calcBottomRight(DEFAULT_SIZE);
      setPosition(initialPos);
    }
  }, [mounted, storedState]);

  // Keep pinned to bottom-right on window resize
  useEffect(() => {
    if (!mounted) return;
    const onResize = () => {
      setPosition(calcBottomRight(size));
    };
    window.addEventListener('resize', onResize);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [mounted, size]);

  // Persist state changes with proper debouncing
  useEffect(() => {
    if (!mounted) return;
    const timer = setTimeout(() => {
      setStoredState({ size, position });
    }, 500); // Increased debounce time
    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer);
    };
  }, [mounted, size, position, setStoredState]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (messageOverride?: string) => {
    const messageContent = (messageOverride ?? input).trim();
    if (messageContent && !isSending) {
      setIsSending(true);
      try {
        await sendMessage(messageContent);
        setInput('');
      } catch (error) {
        // Log error to console - sendMessage propagates errors to ChatContext error boundary
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', error);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMaximize = () => {
    if (isRagAssistant(currentAssistant?.id)) {
      // Open RAG modal via custom event
      window.dispatchEvent(
        new CustomEvent('openRagModal', {
          detail: { openSource: 'popout' },
        }),
      );
      onClose();
    } else {
      if (onMaximize) {
        onMaximize();
      }
      onClose();
    }
  };

  const onResizeStop: RndResizeCallback = (_e, _dir, ref) => {
    const newSize: Size = { width: ref.offsetWidth, height: ref.offsetHeight };
    setSize(newSize);
    setPosition(calcBottomRight(newSize));
  };

  const renderMessageContent = () => {
    if (isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (activeConversation?.data?.id && messages.length === 0) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: 16 }}>
            Loading conversation...
          </Typography>
        </Box>
      );
    }

    if (messages.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="h6">Start a conversation</Typography>
          <Typography variant="body2">
            Type a message below to begin chatting
          </Typography>
          {isBackstageAgentAssistant(currentAssistant?.id) && !input.trim() && (
            <BackstageAgentSuggestedQuestions
              onQuestionClick={question => handleSend(question)}
            />
          )}
        </Box>
      );
    }

    return (
      <>
        {messages.map((message, index) => (
          <ChatMessageComponent
            key={`${message.id}-${index}`}
            message={{
              role: message.role,
              content: message.content,
              timestamp: message.creation_datetime
                ? new Date(message.creation_datetime)
                : undefined,
              agentToolCalls: message.agentToolCalls,
              agentProgress: message.agentProgress,
              isStreamingProgress: isAgentProgressMessageId(message.id),
            }}
            viewMode="markdown"
          />
        ))}
        {isSending && !hasStreamingProgress && (
          <Box display="flex" alignItems="center" justifyContent="center" p={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginLeft: 8 }}>
              AI is thinking...
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </>
    );
  };

  if (!mounted) return null;
  if (!isOpen) return null;

  if (isMinimized) {
    const minimizedNode = (
      <Box className={classes.container}>
        <Box className={classes.minimizedContainer}>
          <Box className={classes.minimizedButton} onClick={handleExpand}>
            <ChatBubbleOutlineIcon />
          </Box>
          <IconButton
            className={classes.minimizedClose}
            size="small"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close chat"
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
    return ReactDOM.createPortal(minimizedNode, document.body);
  }

  const resizeHandles = {
    top: true,
    left: true,
    topLeft: true,
    topRight: false,
    bottom: false,
    right: false,
    bottomLeft: false,
    bottomRight: false,
  };

  const rndStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 1300,
  };

  const expandedNode = (
    <>
      <Rnd
        size={size}
        position={position}
        onResizeStop={onResizeStop}
        bounds="window"
        enableResizing={resizeHandles}
        disableDragging
        style={rndStyle}
        minWidth={300}
        minHeight={400}
        maxWidth={Math.min(800, window.innerWidth - MARGIN * 2)}
        maxHeight={Math.min(900, window.innerHeight - MARGIN * 2)}
      >
        <Paper className={classes.paper}>
          <Box className={classes.header}>
            <Box className={classes.headerInfo}>
              {currentAssistant ? (
                <>
                  <Avatar
                    src={getAssistantLogoUrl(currentAssistant)}
                    alt={currentAssistant.name}
                    className={classes.assistantAvatar}
                    style={
                      shouldShowLocalAssistantIconFallback(currentAssistant)
                        ? { backgroundColor: 'black' }
                        : {}
                    }
                  >
                    {shouldShowLocalAssistantIconFallback(currentAssistant) ? (
                      <AssistantIcon style={{ fontSize: 20, color: 'white' }} />
                    ) : (
                      currentAssistant.name.charAt(0).toUpperCase()
                    )}
                  </Avatar>
                  <Typography variant="subtitle1">
                    {currentAssistant.name}
                  </Typography>
                </>
              ) : (
                <>
                  <Skeleton variant="circle" width={24} height={24} />
                  <Skeleton variant="text" width={100} />
                </>
              )}
            </Box>
            <Box className={classes.headerActions}>
              <Tooltip title="Conversation history">
                <IconButton
                  size="small"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              {isRagAssistant(currentAssistant?.id) && (
                <Tooltip title="Open in RAG Modal">
                  <IconButton size="small" onClick={handleMaximize}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Open in AI Hub">
                <IconButton
                  size="small"
                  onClick={() => {
                    const conversationId = conversation?.id;
                    if (conversationId) {
                      navigate(`/mga/conversation/${conversationId}`);
                    } else {
                      navigate('/mga');
                    }
                    onClose();
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Minimize">
                <IconButton size="small" onClick={handleMinimize}>
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box className={classes.messagesContainer}>
            {renderMessageContent()}
          </Box>

          <Box className={classes.inputContainer}>
            <AssistantSelector disabled={isSending} />
            <TextField
              className={classes.input}
              multiline
              maxRows={4}
              variant="outlined"
              placeholder="Type your message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending || !currentAssistant}
              size="small"
            />
            <IconButton
              color="primary"
              onClick={() => handleSend()}
              disabled={!input.trim() || isSending || !currentAssistant}
            >
              {isSending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      </Rnd>

      <ConversationHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        context="popout"
      />
    </>
  );

  return ReactDOM.createPortal(expandedNode, document.body);
};
